// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Gauge } from '@mui/x-charts/Gauge';

/**
 * MUI X Charts v8 Arc Gauge
 *
 * Semantics:
 * - QoS score of 0 is best.
 * - More negative => worse.
 *
 * We render a "health" gauge in [0..100] where:
 *   health = 100 * (1 - clamp(|score| / maxBad, 0..1))
 *
 * score = 0     => health = 100 (full arc, green)
 * score = -22.2 => health ≈ 77.8 (mostly full, warning-ish)
 */
export default function QoSArcGaugeCard({
  data = {
    SystemQoSScore: -22.2,
    OverallStatus: 'Critical',
    EnergyTreeName: 'Test',
    EnergyTreeId: 96,
  },
  maxBad = 100, // set to your expected "worst" negative magnitude (e.g. 30, 50, 100)
}) {
  const theme = useTheme();
  const { t } = useTranslation('alarms');

  const score = Number(data?.SystemQoSScore ?? 0);
  const health = scoreToHealth(score, maxBad); // 0..100
  const arcColor = healthColor(health, theme);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems="center"
    >
      <Box sx={{ width: 220, height: 140 }}>
        <Gauge
          value={health}
          valueMin={0}
          valueMax={100}
          startAngle={-120}
          endAngle={120}
          cornerRadius="50%"
          text={formatScore(score)}
          sx={{
            // Arc styling (class names are stable across MUI X Charts Gauge)
            '& .MuiGauge-valueArc': { fill: arcColor },
            '& .MuiGauge-referenceArc': {
              fill: theme.palette.action.disabledBackground,
            },
            '& .MuiGauge-valueText': { fontWeight: 800, fontSize: 22 },
          }}
        />
      </Box>

      <Stack spacing={0.25}>
        <Typography variant="body1" color="text.secondary">
          {t('type.energyXplorerOperations.systemQoS.score')}
        </Typography>
        <Typography variant="h6">{data?.EnergyTreeName ?? '—'}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('type.energyXplorerOperations.systemQoS.id')}:{' '}
          {data?.EnergyTreeId ?? '—'}
        </Typography>
      </Stack>
    </Stack>
  );
}

function scoreToHealth(score, maxBad) {
  const max = Math.max(1, Math.abs(Number(maxBad) || 1));
  const s = Number.isFinite(score) ? score : 0;

  // Only negatives are expected; treat positives as "best" unless your domain says otherwise.
  const magnitude = Math.min(Math.abs(s), max); // 0..max
  const health = (1 - magnitude / max) * 100; // 0..100
  return clamp(health, 0, 100);
}

function healthColor(health, theme) {
  if (health >= 80) return theme.palette.success.main;
  if (health >= 50) return theme.palette.warning.main;
  return theme.palette.error.main;
}

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function formatScore(n) {
  if (!Number.isFinite(n)) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}
